// The contextual mini-toolbar that floats above the selected layer — the "power on
// click" tier. Only the few most-relevant actions per layer type; everything else is
// one tap away in the dock ("More"). Anchored in the canvas's display coordinate space.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Copy, ImageIcon, PanelLeft, Sparkles, Trash2 } from 'lucide-react'
import type { CompositionEditor } from './use-composition-editor'
import type { ShapeLayer, TextLayer } from '../types'
import { useConfirmableDelete } from './confirm-delete'
import { ColorPickerSwatch } from './ColorPickerSwatch'
import { ReplacePhotoPopover } from './ReplacePhotoPopover'
import { GLOSSY_DIVIDER, GLOSSY_PILL, GLOSSY_TEXT_BTN } from './toolbar-style'
import { brandAccent, runsInRange } from './composition-ops'
import { italicUnavailable } from './Inspector'
import { activeRangeFor, useTextRangeSelection } from './use-text-range-selection'
import { useLayerOverflowStore } from './use-layer-overflow'

const BRAND_FALLBACK = ['#FAFAF7', '#0B0B0C', '#C6FF3D', '#FFFFFF']

// Clicking a toolbar control must NOT steal focus/selection from the inline text editor (so a
// span stays selected while we restyle it). preventDefault on mousedown keeps the contentEditable
// selection; the click still fires.
const keepSelection = (e: React.MouseEvent) => e.preventDefault()

function palette(editor: CompositionEditor): string[] {
  const p = editor.comp.brand.palette
  return [p.primary, p.secondary, p.accent, p.neutralLight, p.neutralDark, '#FFFFFF', '#000000']
    .filter((c): c is string => !!c)
    .concat(BRAND_FALLBACK)
    .filter((v, i, a) => a.indexOf(v) === i)
}

function Swatches({
  value,
  onPick,
  colors,
}: {
  value: string
  onPick: (c: string) => void
  colors: string[]
}) {
  return (
    <div className="flex items-center gap-1">
      {colors.slice(0, 5).map(c => (
        <button
          key={c}
          type="button"
          title={c}
          onMouseDown={keepSelection}
          onClick={() => onPick(c)}
          className={`h-5 w-5 rounded-full border ${value.toLowerCase() === c.toLowerCase() ? 'border-acid ring-1 ring-acid' : 'border-white/15'}`}
          style={{ background: c }}
        />
      ))}
      {/* Last option: a colorful circle that opens a full color picker. */}
      <ColorPickerSwatch value={value} onPick={onPick} />
    </div>
  )
}

function Btn({
  onClick,
  title,
  disabled,
  children,
}: {
  onClick: () => void
  title?: string
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onMouseDown={keepSelection}
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className={`h-7 min-w-7 justify-center px-1 text-xs font-medium disabled:opacity-30 ${GLOSSY_TEXT_BTN}`}
    >
      {children}
    </button>
  )
}

const Divider = () => <span className={`h-5 ${GLOSSY_DIVIDER}`} />

export function FloatingToolbar({
  editor,
  scale,
  dims,
  onMore,
  onEditText,
  onAskTaka,
  containerRef,
}: {
  editor: CompositionEditor
  scale: number
  dims: { w: number; h: number }
  onMore: () => void
  onEditText: () => void
  /** Pin this layer as the chat context (the canonical inline-menu Ask Taka). */
  onAskTaka?: () => void
  /** The canvas container — the anchor the portaled toolbar positions against. */
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const { confirmingId, requestDelete } = useConfirmableDelete(editor)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [barW, setBarW] = useState(0)
  // Measure the toolbar width each render to clamp its position; the `w !== barW` guard makes
  // this self-stabilizing (no update loop), so the missing-deps warning is a false positive.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const w = wrapRef.current?.offsetWidth ?? 0
    if (w && w !== barW) setBarW(w)
  })
  // Does the selected text layer clip the canvas frame? (measured live by the canvas) — drives
  // the "may clip" warning so the user doesn't ship a cut-off headline.
  const selectedId = editor.selected?.id
  // The toolbar PORTALS to <body> (viewport-fixed) so it can use the WHOLE preview area —
  // the canvas clip wrapper (overflow-hidden rounded media) must never cut it off. Track the
  // anchor through scrolls/resizes/layout-shifts with a reposition tick: only while a layer
  // is actually selected, and rAF-coalesced so app-wide scrolling never storms re-renders.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!selectedId) return
    let raf = 0
    const bump = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        setTick(t => t + 1)
      })
    }
    window.addEventListener('resize', bump)
    document.addEventListener('scroll', bump, true)
    // Layout shifts without a scroll event (e.g. the timeline inspector opening below) move
    // the canvas too — observe the anchor itself.
    const anchorEl = containerRef.current
    const ro = anchorEl ? new ResizeObserver(bump) : null
    if (ro && anchorEl) ro.observe(anchorEl)
    return () => {
      window.removeEventListener('resize', bump)
      document.removeEventListener('scroll', bump, true)
      ro?.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [selectedId, containerRef])
  const clips = useLayerOverflowStore(s => (selectedId ? s.overflowing.has(selectedId) : false))
  const layer = editor.selected
  if (!layer || layer.locked) return null
  const anchor = containerRef.current?.getBoundingClientRect()
  if (!anchor) return null
  // The toolbar STAYS visible while editing a text layer inline — that's how you select a span
  // and restyle just it (the text tools target the current selection). For a non-text layer
  // there is no inline editing, so this only affects text.

  // Anchor above the selection; flip below if it would clip the top of the viewport. Clamp
  // horizontally to the VIEWPORT (the toolbar may spill past the image into the preview area).
  const topPx = anchor.top + layer.y * dims.h * scale
  const margin = 8
  const half = barW / 2
  const rawLeft = anchor.left + (layer.x + layer.w / 2) * dims.w * scale
  const leftPx = Math.max(margin + half, Math.min(window.innerWidth - margin - half, rawLeft))
  const above = topPx > 52
  const colors = palette(editor)
  const confirmingDelete = confirmingId === layer.id
  const showClipWarning = clips && layer.type === 'text'
  const layerBottomPx = topPx + layer.h * dims.h * scale

  return createPortal(
    <>
      {/* Clip warning: the text content runs past the frame, so the published master would cut it
          off. Sits on the OPPOSITE side of the layer from the toolbar (toolbar above → chip below,
          toolbar below → chip above), so the two pills never overlap. */}
      {showClipWarning && (
        <div
          className="pointer-events-none fixed z-40 flex items-center gap-1 rounded-full bg-red-600 px-2 py-1 text-[11px] font-medium text-white shadow"
          style={
            above
              ? { left: leftPx, top: layerBottomPx + 6, transform: 'translate(-50%, 0)' }
              : { left: leftPx, top: topPx - 6, transform: 'translate(-50%, -100%)' }
          }
        >
          <AlertTriangle size={12} />
          Text may clip — shrink it (A−)
        </div>
      )}
      <div
        ref={wrapRef}
        className={`pointer-events-auto fixed z-40 flex items-center gap-1 px-1.5 py-1 ${GLOSSY_PILL}`}
        style={{
          left: leftPx,
          top: above ? topPx - 8 : topPx + layer.h * dims.h * scale + 8,
          transform: `translate(-50%, ${above ? '-100%' : '0'})`,
        }}
        onPointerDown={e => e.stopPropagation()}
      >
        {layer.type === 'text' && (
          <TextTools editor={editor} layer={layer} colors={colors} onEditText={onEditText} />
        )}
        {layer.type === 'shape' && <ShapeTools editor={editor} layer={layer} colors={colors} />}
        {layer.type === 'decor' && (
          <Swatches
            value={layer.color}
            colors={colors}
            onPick={c => editor.updateLayer(layer.id, { color: c } as Partial<typeof layer>)}
          />
        )}
        {(layer.type === 'image' || layer.type === 'logo') && <ImageTools editor={editor} />}

        <Divider />
        <Btn onClick={() => editor.duplicateLayer(layer.id)} title="Duplicate">
          <Copy size={14} />
        </Btn>
        <Btn
          onClick={() => requestDelete(layer)}
          title={confirmingDelete ? 'Click again to delete the layer you added' : 'Delete'}
        >
          <Trash2 size={14} className={confirmingDelete ? 'text-red-400' : undefined} />
        </Btn>
        {onAskTaka && (
          /* The ONE canonical Ask-Taka look shared by every inline menu: stars icon + the words. */
          <Btn onClick={onAskTaka} title="Ask Taka about this layer">
            <Sparkles size={13} />
            Ask Taka
          </Btn>
        )}
        <Divider />
        <Btn onClick={onMore} title="More options (open the design panel)">
          <PanelLeft size={14} />
        </Btn>
      </div>
    </>,
    document.body
  )
}

function TextTools({
  editor,
  layer,
  colors,
  onEditText,
}: {
  editor: CompositionEditor
  layer: TextLayer
  colors: string[]
  onEditText: () => void
}) {
  // When a span is selected in the inline editor, style ops target just that span; otherwise
  // they apply to the whole layer (all runs). One toolbar, two scopes.
  const range = useTextRangeSelection(s => activeRangeFor(s.range, layer.id))
  // "Bold" is true only when EVERY run in scope is already bold — so the first click on a mixed
  // headline (e.g. a 600 lead + 400 focus) BOLDS everything (→700) instead of un-bolding the lead.
  const scopeRuns = range ? runsInRange(layer, range.start, range.end) : layer.runs
  const bold =
    scopeRuns.length > 0 && scopeRuns.every(r => (r.fontWeight ?? layer.fontWeight) >= 600)
  const currentColor = layer.runs[0]?.color ?? layer.color

  // Treatment state reads like Bold: ON only when every run in scope carries it, so the first
  // click on a mixed selection applies rather than removes.
  const italic = scopeRuns.length > 0 && scopeRuns.every(r => r.italic)
  const underlined = scopeRuns.length > 0 && scopeRuns.every(r => r.underlineColor)
  const accent = brandAccent(editor.comp)

  const scaleBy = (f: number) => {
    if (range) editor.scaleTextRange(layer.id, range.start, range.end, f)
    else editor.scaleTextSize(layer.id, f)
  }
  const toggleBold = () => {
    const fontWeight = bold ? 400 : 700
    if (range) editor.restyleTextRange(layer.id, range.start, range.end, { fontWeight })
    else editor.setTextWeight(layer.id, fontWeight)
  }
  const toggleItalic = () => {
    if (range) editor.restyleTextRange(layer.id, range.start, range.end, { italic: !italic })
    else editor.setTextTreatment(layer.id, { italic: !italic })
  }
  const toggleUnderline = () => {
    const underlineColor = underlined ? null : accent
    if (range) editor.restyleTextRange(layer.id, range.start, range.end, { underlineColor })
    else editor.setTextTreatment(layer.id, { underlineColor })
  }
  const setColor = (c: string) => {
    if (range) editor.restyleTextRange(layer.id, range.start, range.end, { color: c })
    else editor.setTextColor(layer.id, c)
  }

  return (
    <>
      <Btn onClick={onEditText} title="Edit text">
        Aa
      </Btn>
      <Divider />
      <Btn onClick={() => scaleBy(1 / 1.12)} title={range ? 'Smaller (selection)' : 'Smaller'}>
        A−
      </Btn>
      <Btn onClick={() => scaleBy(1.12)} title={range ? 'Bigger (selection)' : 'Bigger'}>
        A+
      </Btn>
      <Btn onClick={toggleBold} title={range ? 'Bold (selection)' : 'Bold'}>
        <span style={{ fontWeight: 700 }}>B</span>
      </Btn>
      <Btn
        onClick={toggleItalic}
        disabled={italicUnavailable(layer)}
        title={
          italicUnavailable(layer)
            ? 'Pick a brand font for true italics'
            : range
              ? 'Italic (selection)'
              : 'Italic'
        }
      >
        <span className="italic">I</span>
      </Btn>
      <Btn
        onClick={toggleUnderline}
        title={range ? 'Underline (selection)' : 'Underline (brand accent)'}
      >
        <span className="underline decoration-2">U</span>
      </Btn>
      <Divider />
      <Swatches value={currentColor} colors={colors} onPick={setColor} />
    </>
  )
}

function ShapeTools({
  editor,
  layer,
  colors,
}: {
  editor: CompositionEditor
  layer: ShapeLayer
  colors: string[]
}) {
  const current = layer.fill.kind === 'solid' ? layer.fill.color : '#C6FF3D'
  return (
    <Swatches
      value={current}
      colors={colors}
      onPick={c => editor.setLayerFill(layer.id, { kind: 'solid', color: c })}
    />
  )
}

function ImageTools({ editor }: { editor: CompositionEditor }) {
  const layer = editor.selected
  if (!layer || layer.type !== 'image') return null
  const fit = layer.objectFit === 'cover' ? 'contain' : 'cover'
  return (
    <>
      <ReplacePhotoPopover onPick={attachmentId => editor.replaceImageSrc(layer.id, attachmentId)}>
        <button
          type="button"
          title="Replace photo"
          aria-label="Replace photo"
          className={`h-7 min-w-7 justify-center px-1.5 text-xs font-medium ${GLOSSY_TEXT_BTN}`}
        >
          <ImageIcon size={14} />
        </button>
      </ReplacePhotoPopover>
      <Btn onClick={() => editor.updateLayer(layer.id, { objectFit: fit })} title="Toggle fit">
        {layer.objectFit === 'cover' ? 'Fill' : 'Fit'}
      </Btn>
    </>
  )
}
